import { useEffect, useState } from 'react';
import { Check, Close, InfoOutlined } from '@mui/icons-material';
import { Box, Chip, Divider, IconButton, Typography } from '@mui/material';
import { useDrawer } from 'components/Modals';
import { logos } from 'assets/integracoes';
import Blank from 'assets/blank.png';
import ProdutoModal from 'views/Produtos/Modals/ProdutoModal';

const ImageCarousel = ({ imagens = [], interval = 10000 }) => {
  const [index, setIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    if (!imagens.length) return;

    const timer = setInterval(() => {
      setFade(false);

      setTimeout(() => {
        setIndex((prev) => (prev + 1) % imagens.length);
        setFade(true);
      }, 300);
    }, interval);

    return () => clearInterval(timer);
  }, [imagens, interval]);

  const currentImage = imagens[index] || Blank;

  return (
    <Box
      component="img"
      src={currentImage}
      sx={{
        width: 80,
        height: 80,
        objectFit: 'contain',
        transition: 'opacity 0.5s ease-in-out',
        opacity: fade ? 1 : 0,
      }}
    />
  );
};

const AnuncioCell = ({ row }) => {
  const { openDrawer } = useDrawer();

  const onClickProduto = () => {
    openDrawer(
      <ProdutoModal produto={{ id: row?.produto_id }} />,
      row?.produto
    );
  };

  return (
    <Box py={1} gap={2} width="100%" display="flex" alignItems="stretch">
      <Box
        gap={0.5}
        flexShrink={0}
        display="flex"
        alignItems="center"
        flexDirection="column"
        justifyContent="center"
      >
        <ImageCarousel imagens={row?.imagens || []} />
        <Chip
          size="small"
          label={row?.ativo === 'SIM' ? 'Ativo' : 'Inativo'}
          color={row?.ativo === 'SIM' ? 'success' : 'error'}
          icon={row?.ativo === 'SIM' ? <Check /> : <Close />}
        />
      </Box>
      <Box flex={1} minWidth={0} display="flex" flexDirection="column">
        <Typography
          mb={1}
          noWrap
          color="primary"
          variant="body1"
          fontWeight="bold"
          title={row?.anuncio}
        >
          {row?.anuncio || '-'}
        </Typography>
        <Box flex={1} minHeight={0} display="flex" alignItems="stretch">
          <Box
            pr={2}
            flex={1}
            minWidth={0}
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
          >
            <Typography
              lineHeight={2}
              variant="caption"
              fontWeight="bold"
              color="text.secondary"
              textTransform="uppercase"
            >
              Anúncio
            </Typography>
            <Box display="flex" gap={1}>
              <Typography variant="caption" fontWeight="bold">
                Código:
              </Typography>
              <Typography variant="caption">{row?.codigo || '-'}</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Typography variant="caption" fontWeight="bold">
                SKU:
              </Typography>
              <Typography variant="caption">
                {row?.detalhes?.sku || '-'}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Typography variant="caption" fontWeight="bold">
                Garantia:
              </Typography>
              <Typography variant="caption">
                {row?.garantia || ''} - {row?.descricao_garantia || ''}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Typography variant="caption" fontWeight="bold">
                Estoque:
              </Typography>
              <Typography variant="caption">
                {row?.estoque || 0} {row?.unidade_medida || ''}
              </Typography>
              {Boolean(row?.reserva) && (
                <Chip
                  size="small"
                  label={
                    row?.reserva === 'SIM' ? 'Aceita reserva' : 'Sem reserva'
                  }
                  variant="outlined"
                  color={row?.reserva === 'SIM' ? 'success' : 'error'}
                  icon={row?.reserva === 'SIM' ? <Check /> : <Close />}
                  sx={{
                    height: 20,
                    '& .MuiChip-label': {
                      px: 0.75,
                      fontSize: '0.7rem',
                    },
                    '& .MuiChip-icon': {
                      fontSize: 14,
                    },
                  }}
                />
              )}
            </Box>
            {Boolean(row?.variacoes) && (
              <Box display="flex" gap={1}>
                <Typography variant="caption" fontWeight="bold">
                  Variações:
                </Typography>
                <Typography variant="caption">{row?.variacoes}</Typography>
              </Box>
            )}
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box
            px={2}
            flex={1}
            minWidth={0}
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
          >
            <Box gap={0.5} display="flex" alignItems="center">
              <Typography
                lineHeight={2}
                variant="caption"
                fontWeight="bold"
                color="text.secondary"
                textTransform="uppercase"
              >
                Produto
              </Typography>
              {Boolean(row?.produto_id) && (
                <IconButton
                  size="small"
                  color="primary"
                  sx={{ p: 0.25 }}
                  onClick={onClickProduto}
                >
                  <InfoOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
            <Box display="flex" gap={1}>
              <Typography variant="caption" fontWeight="bold">
                Descrição:
              </Typography>
              <Typography variant="caption">{row?.produto || '-'}</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Typography variant="caption" fontWeight="bold">
                Referência:
              </Typography>
              <Typography variant="caption">
                {row?.referencia || '-'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Divider orientation="vertical" flexItem />
      <Box
        minWidth={130}
        flexShrink={0}
        display="flex"
        alignItems="center"
        justifyContent="flex-end"
      >
        <Typography
          variant="h6"
          color="primary"
          fontWeight="bold"
          whiteSpace="nowrap"
        >
          {(row?.preco || 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
          })}
        </Typography>
      </Box>
    </Box>
  );
};

const columnOverrides = [
  {
    field: 'marketplace',
    renderCell: ({ row }) => {
      const nomeLogo = row?.marketplace?.replace(/\s+/g, '');
      const logo = logos?.[nomeLogo] ?? Blank;
      const imgStyle = { maxWidth: 150, objectFit: 'contain' };

      return (
        <Box py={1} gap={2} flex={1} display="flex" flexDirection="column">
          <Box src={logo} sx={imgStyle} alt={row?.api} component="img" />
          {Boolean(row?.loja) && (
            <Box gap={2} display="flex" flex={1}>
              <Typography variant="caption" fontWeight="bold">
                Loja:
              </Typography>
              <Typography variant="caption" flexWrap="wrap">
                {row?.loja}
              </Typography>
            </Box>
          )}
        </Box>
      );
    },
  },
  {
    field: 'anuncio',
    renderCell: ({ row }) => <AnuncioCell row={row} />,
  },
];

export default columnOverrides;
